import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API, { UPLOADS_URL } from '../api/axios';
import SEO from '../components/SEO';
import Reveal from '../components/ui/Reveal';
import Lightbox from '../components/ui/Lightbox';

const CDN = 'https://www.sawssonipat.com/wp-content/uploads';

const achievementFiles = [
  'IMG_9253.PNG', 'IMG_9254.PNG', 'IMG_9256.PNG',
  'IMG_9263.PNG', 'IMG_9268.PNG', 'IMG_9269.PNG', 'IMG_9272.PNG',
];

const SLIDES = [
  { img: '/images/sliders/1-sonipat-slider.png', alt: 'School Banner 1' },
  { img: '/images/sliders/2-sonipat-slider.png', alt: 'School Banner 2' },
  { img: '/images/sliders/3-sonipat-slider.png', alt: 'School Banner 3' },
];

const TICKER_DEFAULT = { text: '✨ Admissions Open for the 2027–28 Session', url: '' };

const UPLOADS_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

const resolveImg = (src) => {
  if (!src) return src;
  if (src.startsWith('/frontend/')) return src.replace('/frontend/', '/');
  if (src.startsWith('http') || src.startsWith('/')) return src;
  // Handle uploaded files from API (they are just filenames like "1788590513-892169.jpg")
  return UPLOADS_BASE ? `${UPLOADS_BASE}/uploads/${src}` : `/uploads/${src}`;
};

const getYouTubeId = (url) => {
  if (!url) return null;
  const str = String(url).trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(str)) return str;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = str.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

const FACILITY_DATA = [
  { img: '/images/facilities/Junior-Classroom.jpg', title: 'Junior Classrooms' },
  { img: '/images/facilities/Computer-Lab.jpg', title: 'Computer Lab' },
  { img: '/images/facilities/Science-Lab.jpg', title: 'Science Lab' },
  { img: '/images/facilities/Music-Room.jpg', title: 'Music Room' },
  { img: '/images/facilities/Library.jpg', title: 'Library' },
  { img: '/images/facilities/Infrastructure.jpg', title: 'Infrastructure' },
];

const ACTIVITIES = [
  { img: '/images/coactivities/Football.jpg', name: 'Football' },
  { img: '/images/coactivities/Taekwondo.jpg', name: 'Taekwondo' },
  { img: '/images/coactivities/Skating.jpg', name: 'Skating' },
  { img: '/images/coactivities/Swimming-Pool.jpg', name: 'Swimming Pool' },
  { img: '/images/coactivities/Chess.jpg', name: 'Chess' },
  { img: '/images/coactivities/Cricket.jpg', name: 'Cricket' },
  { img: '/images/coactivities/Badminton.jpg', name: 'Badminton' },
  { img: '/images/coactivities/Basketball.jpg', name: 'Basketball' },
  { img: '/images/coactivities/Art-Craft.jpg', name: 'Art & Craft' },
];

const ImagePopupModal = ({ image, delayMs = 5000, onClose, onImageClick }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!image) return;
    const timer = setTimeout(() => setOpen(true), delayMs);
    return () => clearTimeout(timer);
  }, [image, delayMs]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  if (!open || !image) return null;

  const handleClose = () => {
    setOpen(false);
    if (onClose) onClose();
  };

  const handleCardClick = () => {
    if (onImageClick) {
      onImageClick();
    }
    handleClose();
  };

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 animate-fade-in"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label="Popup"
    >
      <div
        className="relative w-fit max-w-[92vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleClose}
          className="absolute -top-3 -right-3 z-10 w-11 h-11 rounded-full bg-white/10 border border-white/20 backdrop-blur text-white flex items-center justify-center hover:bg-white/15 transition-all shadow-lg cursor-pointer"
          aria-label="Close"
        >
          <i className="fas fa-xmark text-lg" />
        </button>
        <div
          className="glass-card overflow-hidden border border-white/15 rounded-2xl shadow-2xl cursor-pointer group"
          onClick={handleCardClick}
          title="Click to Enquire"
        >
          <img
            src={image}
            alt="Admissions Open - St. Andrews World School Sonipat"
            className="w-auto h-auto max-h-[82vh] max-w-full object-contain block group-hover:scale-[1.01] transition-transform duration-300"
          />
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [notices, setNotices] = useState([]);
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [banners, setBanners] = useState([]);
  const [slides, setSlides] = useState([]);
  const [sectionData, setSectionData] = useState({});
  const [highlightsData, setHighlightsData] = useState(null);
  const [galleryAlbums, setGalleryAlbums] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxItems, setLightboxItems] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [awards, setAwards] = useState([]);
  const [awardCategories, setAwardCategories] = useState([]);
  const [facilitiesList, setFacilitiesList] = useState([]);
  const [activitiesList, setActivitiesList] = useState([]);
  const [testimonialsList, setTestimonialsList] = useState([]);
  const [testimonialTab, setTestimonialTab] = useState('all');
  const [activeVideoModal, setActiveVideoModal] = useState(null);
  const navigate = useNavigate();

  const ticker = { ...TICKER_DEFAULT, ...(sectionData.home_ticker || {}) };

  const popupConfig = sectionData.popup || {};
  const isPopupEnabled = popupConfig.enabled !== '0' && popupConfig.enabled !== false;
  const popupImage = popupConfig.image
    ? resolveImg(popupConfig.image)
    : '/images/popup/popuphome.webp';
  const popupDelay = Number(popupConfig.delay_ms) || 5000;
  const popupAction = popupConfig.link_action || 'enquiry';

  const openEnquiryPopup = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (typeof window.openEnquiryPopup === 'function') {
      window.openEnquiryPopup();
    } else {
      const btn = document.querySelector('.npfWidgetButton.npfWidget-ab76b73b357938fdd5c126d3bf550af2') || document.querySelector('.npfWidgetButton');
      if (btn) btn.click();
    }
  };

  const highlightsRef = useRef(null);
  const achievementRef = useRef(null);
  const awardsRef = useRef(null);
  const testimonialRef = useRef(null);
  const autoSlideRef = useRef(null);
  const highlightsSec = highlightsData || sectionData.highlights || null;
  const highlightsItems = (highlightsSec?.items || [])
    .filter(i => Boolean(i && (i.image || i.img)))
    .map(i => ({
      ...i,
      img: resolveImg(i.image || i.img),
      alt: i.alt || 'Highlight',
    }));

  const aboutSec = sectionData.about && (sectionData.about.heading || sectionData.about.paragraphs) ? sectionData.about : null;
  const aboutKicker = aboutSec?.kicker || 'Nurturing Young Minds';
  const aboutHeading = aboutSec?.heading || 'Welcome to St. Andrews World School';
  const sectionClass = (bg) => {
    if (bg === 'blue' || bg === 'navy') return 'section-blue';
    if (bg === 'gray') return 'section-gray';
    if (bg === 'tint') return 'section-tint';
    if (bg === 'gradient') return 'section-gradient';
    return 'section-white';
  };
  const aboutImage = aboutSec?.image || aboutSec?.img ? resolveImg(aboutSec.image || aboutSec.img) : '/images/aboutus.jpeg';
  const aboutVideoId = getYouTubeId(aboutSec?.video_url);
  const aboutMediaType = aboutSec?.media_type || (aboutVideoId && !aboutSec?.image ? 'video' : 'image');
  const showAboutVideo = aboutMediaType === 'video' && Boolean(aboutVideoId);
  const aboutParagraphs = aboutSec?.paragraphs
    ? String(aboutSec.paragraphs).split('\n').map(p => p.trim()).filter(Boolean)
    : [
        "St. Andrews World School, Sonipat is committed to providing a transformative educational journey that extends far beyond textbooks. With a rich legacy across multiple prestigious campuses in India, our mission is to cultivate fearless curiosity, critical thinking, and character in every young learner.",
        "Equipped with future-ready smart classrooms, world-class athletic facilities, and dedicated mentors, we foster an inclusive community where students explore their distinct talents, achieve academic brilliance, and grow into compassionate global leaders.",
        "We warmly welcome students and their families to our school family and invite you to become part of our enduring tradition of excellence.",
      ];
  const aboutBtnText = aboutSec?.button_text || 'Read More';
  const aboutBtnLink = aboutSec?.button_link || '/about-us';

  const achievements = sectionData.achievements?.items?.length
    ? sectionData.achievements.items.map(a => ({ img: resolveImg(a.image || a.img), alt: a.alt || 'Student Achievement' }))
    : achievementFiles.map(f => ({ img: `/images/Student-achievement/${f}`, alt: 'Student Achievement' }));

  const facSec = sectionData.facilities || null;
  const facKicker = facSec?.kicker || 'Campus Infrastructure';
  const facHeading = facSec?.heading || 'Spaces Designed for Growth & Discovery';
  const facDesc = facSec?.description || 'From interactive smart classrooms and high-tech STEM laboratories to dedicated creative arts studios and a multi-sport athletic complex, our Sonipat campus provides a safe, hygienic, and inspiring environment for holistic student development.';
  const facBtnText = sectionData.facilities?.button_text || facSec?.button_text || 'View More Facilities';
  const facBtnLink = sectionData.facilities?.button_link || facSec?.button_link || '/infrastructure';
  const formatTitle = (str) => {
    if (!str) return '';
    const s = String(str).trim();
    if (s === s.toUpperCase()) {
      return s.toLowerCase().replace(/(?:^|\s|-|\/)\b[a-z]/g, c => c.toUpperCase());
    }
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const facilities = (facilitiesList.length ? facilitiesList : (sectionData.facilities?.items?.length ? sectionData.facilities.items : FACILITY_DATA)).map(i => ({
    ...i,
    title: formatTitle(i.title || ''),
    img: resolveImg(i.image || i.img)
  }));
  const visibleFacilities = facilities.slice(0, 6);

  const actSec = sectionData.activities || null;
  const actKicker = actSec?.kicker || 'Beyond the Classroom';
  const actHeading = actSec?.heading || 'Sports, Arts & Cultural Pursuits';
  const actDesc = actSec?.description || 'At St. Andrews World School, education is a blend of intellectual rigour, creative expression, and athletic stamina. From competitive football, swimming, and martial arts to classical music, theatre, and debate, we empower students to cultivate teamwork, discipline, and lifelong confidence.';
  const actBtnText = sectionData.activities?.button_text || actSec?.button_text || 'View More Activities';
  const actBtnLink = sectionData.activities?.button_link || actSec?.button_link || '/extra-curricular-activities';

  const awardsSec = sectionData.awards || null;
  const awardsKicker = awardsSec?.kicker || 'National & Global Laurels';
  const awardsHeading = awardsSec?.heading || 'Prestigious Honours & Accolades';
  const awardsDesc = awardsSec?.description || 'Recognized across leading national and international educational forums for academic distinction, visionary leadership, and unwavering commitment to child-centric learning.';

  const aboutBg = 'section-white';
  const highlightsBg = 'section-gray border-y border-gray-100';
  const achievementsBg = 'section-white';
  const awardsBg = 'section-gray border-y border-gray-100';
  const facilitiesBg = (facSec?.background && sectionClass(facSec.background)) || 'section-blue';
  const isFacilitiesBlue = facilitiesBg === 'section-blue';
  const activitiesBg = 'section-white';
  const testimonialsBg = 'section-gray border-t border-gray-100';
  const activities = (activitiesList.length ? activitiesList : (sectionData.activities?.items?.length ? sectionData.activities.items : ACTIVITIES)).map(i => ({
    ...i,
    name: i.title || i.name,
    img: resolveImg(i.image || i.img)
  }));
  const visibleActivities = activities.slice(0, 6);

  const DEFAULT_TESTIMONIALS = [
    {
      id: 1,
      type: 'text',
      name: 'Mrs. Meenakshi Sharma',
      designation: 'Parent of Aarav Sharma (Grade 5)',
      quote: 'St. Andrews World School has provided an exceptional environment for our son. The dedicated teachers, focus on holistic development, and world-class sports facilities have helped him blossom into a confident, inquisitive learner.',
      video_url: null,
      youtube_id: null,
      image: null,
      rating: 5,
    },
    {
      id: 2,
      type: 'video',
      name: 'Dr. Rajesh & Dr. Sunita Verma',
      designation: 'Parents of Ananya Verma (Grade 8)',
      quote: 'Choosing St. Andrews Sonipat was the best decision for our daughter\'s academic and emotional growth. The values and personalized care are unmatched.',
      video_url: 'https://www.youtube.com/watch?v=ScMzIvxBSi4',
      youtube_id: 'ScMzIvxBSi4',
      image: null,
      rating: 5,
    },
    {
      id: 3,
      type: 'text',
      name: 'Vikramaditya Rathore',
      designation: 'Alumnus (Batch of 2022)',
      quote: 'The mentorship and leadership opportunities I received at St. Andrews World School shaped my path to securing admission at a top engineering institute. Truly grateful for the invaluable foundation.',
      video_url: null,
      youtube_id: null,
      image: null,
      rating: 5,
    },
    {
      id: 4,
      type: 'video',
      name: 'Pooja Malhotra',
      designation: 'Mother of Reyansh (Grade 2)',
      quote: 'The early years curriculum at SAWS Sonipat is thoughtfully structured. The emphasis on hands-on activities, experiential learning, and child safety gives us complete peace of mind.',
      video_url: 'https://www.youtube.com/watch?v=ysz5S6PUM-U',
      youtube_id: 'ysz5S6PUM-U',
      image: null,
      rating: 5,
    },
  ];

  const testSec = sectionData.testimonials || sectionData.home_testimonials || null;
  const testKicker = testSec?.kicker || 'Real Stories & Experiences';
  const testHeading = testSec?.heading || 'What Parents & Students Say';
  const testDesc = testSec?.description || 'Heartfelt reflections, genuine feedback, and inspiring experiences shared by parents and students on their transformative journey at St. Andrews World School.';
  const testBg = (testSec?.background && sectionClass(testSec.background)) || 'section-white';

  const rawTestimonials = testimonialsList.length ? testimonialsList : DEFAULT_TESTIMONIALS;
  const testimonials = rawTestimonials.map(t => ({
    ...t,
    youtube_id: t.youtube_id || getYouTubeId(t.video_url),
  }));

  const filteredTestimonials = testimonials.filter(t => {
    if (testimonialTab === 'text') return t.type === 'text';
    if (testimonialTab === 'video') return t.type === 'video';
    return true;
  });

  const hasBothTestimonialTypes = testimonials.some(t => t.type === 'video') && testimonials.some(t => t.type === 'text');

  const scrollHighlights = (dir) => {
    if (highlightsRef.current) {
      highlightsRef.current.scrollBy({ left: dir * (highlightsRef.current.offsetWidth / 4 + 20), behavior: 'smooth' });
    }
  };
  const scrollAchievement = (dir) => {
    if (achievementRef.current) {
      achievementRef.current.scrollBy({ left: dir * 320, behavior: 'smooth' });
    }
  };
  const scrollAwards = (dir) => {
    if (awardsRef.current) {
      const card = awardsRef.current.querySelector('.award-carousel-card');
      const step = card ? card.offsetWidth + 24 : awardsRef.current.offsetWidth / 4;
      awardsRef.current.scrollBy({ left: dir * step, behavior: 'smooth' });
    }
  };

  const scrollTestimonials = (dir) => {
    if (testimonialRef.current) {
      const card = testimonialRef.current.querySelector('.testimonial-slide-card');
      const step = card ? card.offsetWidth + 24 : 380;
      testimonialRef.current.scrollBy({ left: dir * step, behavior: 'smooth' });
    }
  };

  const handleTestimonialTab = (tab) => {
    setTestimonialTab(tab);
    if (testimonialRef.current) {
      testimonialRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (filteredTestimonials.length <= 1) return;
    const interval = setInterval(() => {
      if (testimonialRef.current) {
        const maxScroll = testimonialRef.current.scrollWidth - testimonialRef.current.clientWidth;
        if (testimonialRef.current.scrollLeft >= maxScroll - 15) {
          testimonialRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          const card = testimonialRef.current.querySelector('.testimonial-slide-card');
          const step = card ? card.offsetWidth + 24 : 380;
          testimonialRef.current.scrollBy({ left: step, behavior: 'smooth' });
        }
      }
    }, 4500);
    return () => clearInterval(interval);
  }, [filteredTestimonials.length]);

  useEffect(() => {
    if (highlightsItems.length <= 4) return;
    autoSlideRef.current = setInterval(() => {
      if (highlightsRef.current) {
        const maxScroll = highlightsRef.current.scrollWidth - highlightsRef.current.clientWidth;
        if (highlightsRef.current.scrollLeft >= maxScroll - 10) {
          highlightsRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          highlightsRef.current.scrollBy({ left: highlightsRef.current.offsetWidth / 4 + 20, behavior: 'smooth' });
        }
      }
    }, 3000);
    return () => clearInterval(autoSlideRef.current);
  }, [highlightsItems.length]);

  useEffect(() => {
    if (achievements.length <= 1) return;
    const interval = setInterval(() => {
      if (achievementRef.current) {
        const maxScroll = achievementRef.current.scrollWidth - achievementRef.current.clientWidth;
        if (achievementRef.current.scrollLeft >= maxScroll - 10) {
          achievementRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          achievementRef.current.scrollBy({ left: achievementRef.current.clientWidth, behavior: 'smooth' });
        }
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [achievements.length]);

  useEffect(() => {
    // Dedicated direct fetch for highlights section from admin
    API.get('/home-sections/highlights')
      .then(res => {
        const content = res.data?.data?.content;
        if (content) setHighlightsData(content);
      })
      .catch(() => {});

    const fetchData = async () => {
      try {
        const [noticeRes, eventsRes, newsRes, blogRes, bannerRes, sectionRes, awardsRes, facilitiesRes, activitiesRes, testimonialsRes] = await Promise.all([
          API.get('/notices/latest').catch(() => ({ data: { data: [] } })),
          API.get('/events/active').catch(() => ({ data: { data: [] } })),
          API.get('/notices/news').catch(() => ({ data: { data: [] } })),
          API.get('/blogs/latest').catch(() => ({ data: { data: [] } })),
          API.get('/banners/active').catch(() => ({ data: { data: [] } })),
          API.get('/home-sections').catch(() => ({ data: { data: {} } })),
          API.get('/awards/public').catch(() => ({ data: { data: [], categories: [] } })),
          API.get('/facilities/public').catch(() => ({ data: { data: [] } })),
          API.get('/activities/public').catch(() => ({ data: { data: [] } })),
          API.get('/testimonials/public').catch(() => ({ data: { data: [] } })),
        ]);
        if (noticeRes?.data?.data && noticeRes.data.data.length) setNotices(noticeRes.data.data);
        if (eventsRes?.data?.data && eventsRes.data.data.length) setEvents(eventsRes.data.data);
        if (newsRes?.data?.data && newsRes.data.data.length) setNews(newsRes.data.data);
        if (blogRes?.data?.data && blogRes.data.data.length) setBlogs(blogRes.data.data);
        if (facilitiesRes?.data?.data && facilitiesRes.data.data.length) setFacilitiesList(facilitiesRes.data.data);
        if (activitiesRes?.data?.data && activitiesRes.data.data.length) setActivitiesList(activitiesRes.data.data);
        if (testimonialsRes?.data?.data && testimonialsRes.data.data.length) setTestimonialsList(testimonialsRes.data.data);
        if (awardsRes?.data?.data) {
          setAwards(awardsRes.data.data);
          setAwardCategories(awardsRes.data.categories || []);
        }
        if (bannerRes?.data?.data && bannerRes.data.data.length) {
          setBanners(bannerRes.data.data);
          const mapped = bannerRes.data.data.map(b => ({
            img: resolveImg(b.image || b.img),
            alt: b.title || 'Banner',
            link: b.link,
            btn_text: b.btn_text,
          }));
          setSlides(mapped);
        } else {
          setSlides(SLIDES);
        }
        if (sectionRes?.data?.data) {
          setSectionData(sectionRes.data.data);
          if (sectionRes.data.data.highlights) {
            setHighlightsData(sectionRes.data.data.highlights);
          }
        }
        try {
          const galleryRes = await API.get('/galleries/public?limit=8');
          if (galleryRes.data.data && galleryRes.data.data.length) setGalleryAlbums(galleryRes.data.data);
        } catch (gErr) { console.error('Gallery fetch error:', gErr); }
      } catch (err) { console.error(err); }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    const interval = setInterval(() => setActiveSlide(prev => (prev + 1) % slides.length), 3000);
    return () => clearInterval(interval);
  }, [slides.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setActiveVideoModal(null);
    };
    if (activeVideoModal) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeVideoModal]);

  const prevSlide = () => setActiveSlide(prev => (prev - 1 + slides.length) % slides.length);
  const nextSlide = () => setActiveSlide(prev => (prev + 1) % slides.length);

  const openLightbox = (items, index = 0) => {
    const safe = Array.isArray(items) ? items.filter(Boolean) : [];
    if (safe.length === 0) return;
    setLightboxItems(safe);
    setLightboxIndex(Math.max(0, Math.min(index, safe.length - 1)));
    setLightboxOpen(true);
  };

  return (
    <>
      <SEO slug="home" title="Home"
        description="St. Andrews World School, Sonipat - premium CBSE education with modern learning, holistic development, and world-class facilities. Admissions open for 2026-27."
        keywords="CBSE school in Sonipat, best school in Sonipat, admissions 2026-27, St. Andrews World School, CBSE curriculum, smart classrooms, sports, activities"
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'School',
            name: 'St. Andrews World School',
            url: (process.env.REACT_APP_SITE_URL || 'https://sawssonipat.com'),
            address: { '@type': 'PostalAddress', addressLocality: 'Sonipat', addressRegion: 'Haryana', addressCountry: 'IN' },
          },
        ]}
      />

      {/* SEO H1 - visually hidden */}
      <h1 className="sr-only">St. Andrews World School, Sonipat — CBSE School in Sonipat</h1>

      {/* HERO - DESKTOP */}
      <section className="slider-section hero-slider relative overflow-hidden bg-midnight hidden md:block md:min-h-[340px]">
        <div className="relative">
          {slides.map((slide, i) => (
            <div
              key={i}
              className={`transition-opacity duration-700 ${i === activeSlide ? 'block' : 'hidden'}`}
              aria-hidden={i !== activeSlide}
            >
              <img src={slide.img} alt={slide.alt} className="w-full h-auto" loading={i === activeSlide ? 'eager' : 'lazy'} />
            </div>
          ))}
        </div>

        {slides.length > 1 && (
          <>
            <div className="absolute bottom-7 left-1/2 -translate-x-1/2 z-[3] flex gap-3">
              {slides.map((_, i) => (
                <button
                  key={i}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`rounded-full border-none cursor-pointer transition-all duration-500 ${
                    i === activeSlide
                      ? 'bg-secondary w-8 h-2.5 shadow-sm shadow-secondary/40'
                      : 'bg-white/40 w-2.5 h-2.5 hover:bg-white/60'
                  }`}
                  onClick={() => setActiveSlide(i)}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {/* HERO - MOBILE */}
      <section className="slider-section hero-slider block md:hidden bg-midnight">
        <div className="relative">
          {slides.map((slide, i) => (
            <div key={i} className={`transition-opacity duration-500 ${i === activeSlide ? 'block' : 'hidden'}`}>
              <img src={slide.img} alt={slide.alt} className="w-full h-auto" loading="eager" />
            </div>
          ))}
          {slides.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[3] flex gap-2">
              {slides.map((_, i) => (
                <button key={i} aria-label={`Go to slide ${i + 1}`}
                  className={`rounded-full border-none cursor-pointer transition-all duration-500 ${
                    i === activeSlide ? 'bg-secondary w-6 h-2 shadow-sm' : 'bg-white/50 w-2 h-2'
                  }`}
                  onClick={() => setActiveSlide(i)} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ADMISSIONS TICKER */}
      <section className="ticker-section bg-gradient-to-r from-primary via-primary-dark to-primary text-center py-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(27,49,96,0.15)_0%,transparent_50%)]"></div>
        <div className="container relative">
          <a
            href={ticker.url || '#'}
            onClick={ticker.url ? undefined : openEnquiryPopup}
            className="text-white/90 hover:text-white text-[14px] md:text-xl font-semibold no-underline tracking-wide transition-colors cursor-pointer inline-flex items-center gap-2"
          >
            <span>{ticker.text}</span>
            <i className="fas fa-arrow-right text-secondary"></i>
          </a>
        </div>
      </section>

      {/* ABOUT ST. ANDREWS */}
      <Reveal>
        <section className={`py-12 ${aboutBg}`}>
          <div className="container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-lg bg-black">
                  {showAboutVideo ? (
                    <div className="relative w-full aspect-video min-h-[260px] sm:min-h-[320px]">
                      <iframe
                        src={`https://www.youtube.com/embed/${aboutVideoId}?autoplay=1&mute=1&loop=1&playlist=${aboutVideoId}&playsinline=1&rel=0&modestbranding=1`}
                        title="About St. Andrews World School"
                        className="w-full h-full absolute inset-0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <img
                      src={aboutImage}
                      alt="About St. Andrews World School"
                      className="w-full h-auto object-cover"
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-secondary/20 rounded-2xl -z-10"></div>
              </div>
              <div>
                <span className="kicker">{aboutKicker}</span>
                <h2 className="h-section mt-2 mb-3">{aboutHeading}</h2>
                <div className="w-14 h-[3px] bg-gradient-to-r from-secondary to-secondary-light rounded-full mb-6"></div>
                {aboutParagraphs.map((p, i) => (
                  <p key={i} className="text-body leading-relaxed mb-4" style={{ WebkitHyphens: 'none', msHyphens: 'none', hyphens: 'none' }}>{p}</p>
                ))}
                <Link to={aboutBtnLink} className="inline-flex items-center gap-2 bg-primary text-white px-7 py-3 rounded-full font-semibold text-sm hover:bg-primary-dark transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5">{aboutBtnText} <i className="fas fa-arrow-right text-[11px]"></i></Link>
              </div>
            </div>
          </div>
        </section>
      </Reveal>

      {/* HIGHLIGHTS */}
      {highlightsSec && (highlightsItems.length > 0 || highlightsSec.heading) && (
        <Reveal>
          <section className={`py-12 ${highlightsBg}`}>
            <div className="container">
              <div className="text-center mb-8">
                <div className="kicker">{highlightsSec?.kicker || 'Campus Life in Action'}</div>
                <h2 className="h-section mt-2">{highlightsSec?.heading || 'Cherished Moments & Milestones'}</h2>
                <div className="w-14 h-[3px] bg-gradient-to-r from-secondary to-secondary-light rounded-full mx-auto mt-3"></div>
              </div>

              {(highlightsSec.subtitle || highlightsSec.award_title) && (
                <div className="text-center mb-8">
                  {highlightsSec.subtitle && (
                    <p className="text-body max-w-3xl mx-auto text-center">
                      {highlightsSec.subtitle}
                    </p>
                  )}
                  {highlightsSec.award_title && (
                    <h2 className="h-section mt-2">{highlightsSec.award_title}</h2>
                  )}
                </div>
              )}

              {highlightsItems.length > 0 && (
                <div className="relative">
                  <div
                    ref={highlightsRef}
                    className="flex gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 hide-scrollbar justify-center lg:justify-start"
                  >
                    {highlightsItems.map((item, idx) => (
                      <button
                        key={item.img || idx}
                        type="button"
                        className="snap-start shrink-0 w-full sm:w-1/2 lg:w-1/3 rounded-lg overflow-hidden border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all group cursor-pointer"
                        onClick={() => openLightbox(highlightsItems, idx)}
                      >
                        <div className="relative">
                          <img
                            src={item.img}
                            alt={item.alt || 'Highlight'}
                            className="w-full h-auto object-contain transition-transform duration-700 group-hover:scale-[1.03]"
                            loading="lazy"
                            onError={(e) => {
                              if (!e.target.dataset.triedFallback) {
                                e.target.dataset.triedFallback = 'true';
                                e.target.src = '/images/page-banners/why-choose-us.jpg';
                              }
                            }}
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {highlightsItems.length > 3 && (
                    <>
                      <button
                        onClick={() => scrollHighlights(-1)}
                        aria-label="Previous"
                        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 w-10 h-10 rounded-full bg-white shadow-md border border-gray-200 text-primary flex items-center justify-center hover:bg-gray-50 transition-all cursor-pointer"
                      >
                        <i className="fas fa-chevron-left text-sm"></i>
                      </button>
                      <button
                        onClick={() => scrollHighlights(1)}
                        aria-label="Next"
                        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 w-10 h-10 rounded-full bg-white shadow-md border border-gray-200 text-primary flex items-center justify-center hover:bg-gray-50 transition-all cursor-pointer"
                      >
                        <i className="fas fa-chevron-right text-sm"></i>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </section>
        </Reveal>
      )}

      {/* STUDENT ACHIEVEMENTS */}
      <Reveal>
        <section className={`py-12 ${achievementsBg}`}>
          <div className="container">
            <div className="text-center mb-10">
              <div className="kicker">{sectionData.achievements?.kicker || 'Pride & Glory'}</div>
              <h2 className="h-section mt-2">{sectionData.achievements?.heading || 'Celebrating Student Excellence'}</h2>
              <div className="w-14 h-[3px] bg-gradient-to-r from-secondary to-secondary-light rounded-full mx-auto mt-3"></div>
            </div>
            <div className="relative">
              <div ref={achievementRef} className="flex gap-4 overflow-x-auto scroll-smooth hide-scrollbar justify-center lg:justify-start" style={{ scrollBehavior: 'smooth' }}>
                {achievements.map((file, idx) => (
                  <Reveal key={file.img || file.image} delayMs={idx * 50} className="flex-shrink-0 w-full md:w-[300px]">
                    <button
                      type="button"
                      onClick={() => openLightbox(achievements, idx)}
                      className="group w-full rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 bg-white block"
                    >
                      <div className="relative">
                        <img
                          src={file.img}
                          alt={file.alt || 'Student Achievement'}
                          className="w-full h-auto object-contain transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-white/90 text-primary flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
                            <i className="fas fa-expand text-sm" />
                          </div>
                        </div>
                      </div>
                    </button>
                  </Reveal>
                ))}
              </div>
              {achievements.length > 3 && (
                <>
                  <button onClick={() => scrollAchievement(-1)} aria-label="Previous" className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 w-10 h-10 rounded-full bg-white shadow-md border border-gray-200 text-primary flex items-center justify-center hover:bg-gray-50 transition-all"><i className="fas fa-chevron-left text-sm"></i></button>
                  <button onClick={() => scrollAchievement(1)} aria-label="Next" className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 w-10 h-10 rounded-full bg-white shadow-md border border-gray-200 text-primary flex items-center justify-center hover:bg-gray-50 transition-all"><i className="fas fa-chevron-right text-sm"></i></button>
                </>
              )}
            </div>
          </div>
        </section>
      </Reveal>

      {/* AWARDS & ACCOLADES */}
      {awards.length > 0 && (
        <Reveal>
          <section className={`py-12 ${awardsBg} relative overflow-hidden`}>
            <div className="container">
              <div className="text-center mb-8">
                <div className="kicker">{awardsKicker}</div>
                <h2 className="h-section mt-2">{awardsHeading}</h2>
                <div className="w-14 h-[3px] bg-gradient-to-r from-secondary to-secondary-light rounded-full mx-auto mt-3"></div>
                <p className="text-body text-base max-w-[48rem] mx-auto mt-3">
                  {awardsDesc}
                </p>
              </div>

              {/* Awards Carousel / Cards */}
              <div className="relative">
                <div
                  ref={awardsRef}
                  className="flex gap-6 overflow-x-auto scroll-smooth hide-scrollbar pb-4 pt-1 snap-x snap-mandatory"
                  style={{ scrollBehavior: 'smooth' }}
                >
                  {awards.map((item, idx) => {
                    const imgSrc = item.image ? `${UPLOADS_URL}/${item.image}` : null;
                    return (
                      <div key={item.id} className="award-carousel-card flex-shrink-0 w-full sm:w-[calc((100%-24px)/2)] md:w-[calc((100%-48px)/3)] lg:w-[calc((100%-72px)/4)] snap-start">
                        <div className="group h-full bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col hover:-translate-y-1">
                          {/* Image or Trophy Icon */}
                          <div
                            className="h-72 sm:h-80 md:h-88 bg-gradient-to-tr from-amber-50/50 via-gray-50 to-blue-50/30 flex items-center justify-center relative overflow-hidden cursor-pointer border-b border-gray-100"
                            onClick={() => imgSrc && openLightbox([{ img: imgSrc, alt: item.name }], 0)}
                          >
                            {imgSrc ? (
                              <>
                                <img
                                  src={imgSrc}
                                  alt={item.name}
                                  className="w-full h-full object-cover sm:object-contain p-2 sm:p-3 transition-transform duration-500 group-hover:scale-105"
                                  loading="lazy"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.querySelector('.award-fallback-icon')?.classList.remove('hidden');
                                  }}
                                />
                                <div className="award-fallback-icon hidden text-amber-500 text-5xl">
                                  <i className="fas fa-trophy" />
                                </div>
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors duration-300 flex items-center justify-center">
                                  <div className="w-11 h-11 rounded-full bg-white/95 text-primary flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-3 group-hover:translate-y-0 shadow-lg">
                                    <i className="fas fa-expand text-sm" />
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-16 h-16 rounded-full bg-amber-100/80 text-amber-600 flex items-center justify-center text-3xl shadow-inner group-hover:scale-110 transition-transform">
                                <i className="fas fa-award" />
                              </div>
                            )}

                            {/* Year badge */}
                            {item.year && (
                              <span className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2.5 py-0.5 rounded-full text-xs font-bold text-primary shadow-sm border border-gray-100">
                                {item.year}
                              </span>
                            )}
                          </div>

                          {/* Details */}
                          <div className="p-5 flex-1 flex flex-col justify-between">
                            <div>
                              {item.category && (
                                <span className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-primary/10 text-primary mb-2">
                                  {item.category.name}
                                </span>
                              )}
                              <h3 className="font-bold text-gray-900 text-base leading-snug group-hover:text-primary transition-colors line-clamp-2">
                                {item.name}
                              </h3>
                              {item.awarded_by && (
                                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5">
                                  <i className="fas fa-certificate text-secondary text-[11px]" />
                                  <span>{item.awarded_by}</span>
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Arrows */}
                {awards.length > 3 && (
                  <>
                    <button
                      type="button"
                      onClick={() => scrollAwards(-1)}
                      aria-label="Previous Awards"
                      className="absolute -left-2 sm:-left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-lg border border-gray-200 text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-all z-20 group"
                    >
                      <i className="fas fa-chevron-left text-sm group-hover:-translate-x-0.5 transition-transform" />
                    </button>
                    <button
                      type="button"
                      onClick={() => scrollAwards(1)}
                      aria-label="Next Awards"
                      className="absolute -right-2 sm:-right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-lg border border-gray-200 text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-all z-20 group"
                    >
                      <i className="fas fa-chevron-right text-sm group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  </>
                )}
              </div>

              {/* View All Button */}
              <div className="text-center mt-8">
                <Link
                  to="/recognition"
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-primary text-primary hover:bg-primary hover:text-white transition-all text-xs font-semibold uppercase tracking-wider shadow-sm"
                >
                  <span>View All Recognition & Awards</span>
                  <i className="fas fa-arrow-right text-xs" />
                </Link>
              </div>
            </div>
          </section>
        </Reveal>
      )}

      {/* FACILITIES */}
      <Reveal>
        <section className={`py-12 relative overflow-hidden ${facilitiesBg}`}>
          {isFacilitiesBlue && (
            <>
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-56 bg-secondary/15 blur-[110px] pointer-events-none rounded-full"></div>
              <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-primary-light/30 blur-[100px] pointer-events-none rounded-full"></div>
            </>
          )}
          <div className="container relative z-10">
            <div className="text-center mb-12 md:mb-14">
              <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full text-xs font-semibold uppercase tracking-widest bg-secondary/15 text-secondary-light border border-secondary/30 mb-3 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-secondary-light animate-pulse"></span>
                {facKicker}
              </div>
              <h2 className={`h-section mt-1 ${isFacilitiesBlue ? '!text-white' : ''}`}>
                {facHeading}
              </h2>
              <div className="w-16 h-[3px] bg-gradient-to-r from-secondary to-secondary-light rounded-full mx-auto mt-3 shadow-sm"></div>
              <p className={`text-base md:text-[17px] leading-relaxed max-w-[75rem] mx-auto mt-4 font-normal ${isFacilitiesBlue ? 'text-blue-100/90' : 'text-body'}`}>
                {facDesc}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {visibleFacilities.map((f, i) => (
                <Reveal key={f.title || i} delayMs={i * 70}>
                  <button
                    type="button"
                    className="relative group w-full text-left cursor-pointer focus:outline-none"
                    onClick={() => openLightbox(visibleFacilities.map(x => ({ img: x.img, alt: x.title })), i)}
                  >
                    <div className={`relative overflow-hidden rounded-2xl transition-all duration-500 group-hover:-translate-y-1.5 ${
                      isFacilitiesBlue
                        ? 'bg-[#0f1d3a] border border-white/15 shadow-xl group-hover:border-secondary/60 group-hover:shadow-[0_16px_36px_rgba(0,0,0,0.45)]'
                        : 'bg-white border border-gray-100 shadow-sm group-hover:shadow-lg'
                    }`}>
                      <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-900">
                        <img
                          src={f.img}
                          alt={f.title}
                          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1428] via-[#0a1428]/45 to-transparent opacity-85 group-hover:opacity-95 transition-opacity duration-300"></div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between gap-3">
                        <div>
                          <h3 className="text-white text-lg sm:text-xl font-serif font-bold tracking-wide group-hover:text-secondary-light transition-colors drop-shadow-sm capitalize">
                            {f.title}
                          </h3>
                        </div>
                        <div className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-md border border-white/25 flex items-center justify-center text-white shrink-0 group-hover:bg-secondary group-hover:text-primary group-hover:border-secondary transition-all duration-300 shadow-md">
                          <i className="fas fa-arrow-up-right-from-square text-xs group-hover:scale-110 transition-transform"></i>
                        </div>
                      </div>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>

            <div className="text-center mt-12 md:mt-14">
              <Link
                to={facBtnLink}
                className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full bg-secondary text-primary font-semibold text-sm hover:bg-secondary-light transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                <span>{facBtnText}</span>
                <i className="fas fa-arrow-right text-xs"></i>
              </Link>
            </div>
          </div>
        </section>
      </Reveal>

      {/* CO-CURRICULAR ACTIVITIES */}
      <Reveal>
        <section className={`py-12 ${activitiesBg}`}>
          <div className="container">
            <div className="text-center mb-10">
              <div className="kicker">{actKicker}</div>
              <h2 className="h-section mt-2">{actHeading}</h2>
              <div className="w-14 h-[3px] bg-gradient-to-r from-secondary to-secondary-light rounded-full mx-auto mt-3"></div>
            </div>
            <p className="text-body text-center max-w-[75rem] mx-auto mb-10">
              {actDesc}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {visibleActivities.map((activity, i) => (
                activity.img ? (
                  <Reveal key={activity.name || i} delayMs={i * 50}>
                    <Link
                      to={actBtnLink}
                      className="block relative group rounded-2xl overflow-hidden bg-white shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5"
                    >
                      <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-900">
                        <img
                          src={activity.img}
                          alt={activity.name}
                          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1428] via-[#0a1428]/40 to-transparent opacity-85 group-hover:opacity-95 transition-opacity duration-300"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between gap-3">
                          <div>
                            <h3 className="text-white text-lg sm:text-xl font-serif font-bold tracking-wide group-hover:text-secondary-light transition-colors drop-shadow-sm">
                              {activity.name}
                            </h3>
                          </div>
                          <div className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-md border border-white/25 flex items-center justify-center text-white shrink-0 group-hover:bg-secondary group-hover:text-primary group-hover:border-secondary transition-all duration-300 shadow-md">
                            <i className="fas fa-arrow-up-right-from-square text-xs group-hover:scale-110 transition-transform"></i>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </Reveal>
                ) : (
                  <Reveal key={activity.name || i} delayMs={i * 50}>
                    <Link
                      to={actBtnLink}
                      className="block relative group rounded-2xl overflow-hidden bg-white text-center p-8 shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5"
                    >
                      <div className="w-[70px] h-[70px] rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4 text-primary text-2xl group-hover:bg-primary group-hover:text-white transition-all">
                        <i className={`fas ${activity.icon || 'fa-medal'}`}></i>
                      </div>
                      <h4 className="text-primary text-lg font-serif font-semibold">{activity.name}</h4>
                    </Link>
                  </Reveal>
                )
              ))}
            </div>

            <div className="text-center mt-12 md:mt-14">
              <Link
                to={actBtnLink}
                className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full bg-primary text-white font-semibold text-sm hover:bg-primary-dark transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5"
              >
                <span>{actBtnText}</span>
                <i className="fas fa-arrow-right text-xs"></i>
              </Link>
            </div>
          </div>
        </section>
      </Reveal>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <Reveal>
          <section className={`py-12 ${testimonialsBg} relative overflow-hidden`}>
            {/* Subtle background ambient lights */}
            <div className="absolute top-0 right-1/4 w-96 h-96 bg-secondary/5 blur-[120px] pointer-events-none rounded-full" />
            <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-primary/5 blur-[120px] pointer-events-none rounded-full" />

            <div className="container relative z-10">
              <div className="text-center mb-10">
                <div className="kicker">{testKicker}</div>
                <h2 className="h-section mt-2">{testHeading}</h2>
                <div className="w-14 h-[3px] bg-gradient-to-r from-secondary to-secondary-light rounded-full mx-auto mt-3" />
                <p className="text-body text-base max-w-[75rem] text-center mx-auto mt-3">
                  {testDesc}
                </p>

                {/* Filter Tabs (when both text & video exist) */}
                {hasBothTestimonialTypes && (
                  <div className="inline-flex items-center p-1 bg-white rounded-full border border-gray-200 mt-6 shadow-2xs">
                    <button
                      type="button"
                      onClick={() => handleTestimonialTab('all')}
                      className={`px-5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                        testimonialTab === 'all'
                          ? 'bg-primary text-white shadow-xs'
                          : 'text-gray-600 hover:text-primary'
                      }`}
                    >
                      All ({testimonials.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTestimonialTab('text')}
                      className={`px-5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                        testimonialTab === 'text'
                          ? 'bg-primary text-white shadow-xs'
                          : 'text-gray-600 hover:text-primary'
                      }`}
                    >
                      Parent Reviews
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTestimonialTab('video')}
                      className={`px-5 py-1.5 rounded-full text-xs font-semibold transition-all inline-flex items-center gap-1.5 ${
                        testimonialTab === 'video'
                          ? 'bg-primary text-white shadow-xs'
                          : 'text-gray-600 hover:text-primary'
                      }`}
                    >
                      <i className="fab fa-youtube text-red-500 text-xs"></i>
                      <span>Video Stories</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Testimonials Slider / Carousel */}
              <div className="relative px-2 sm:px-4">
                <div
                  ref={testimonialRef}
                  className="flex gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory hide-scrollbar pb-6 pt-2 items-stretch"
                  style={{ scrollBehavior: 'smooth' }}
                >
                  {filteredTestimonials.map((t, idx) => (
                    <div
                      key={t.id || idx}
                      className="testimonial-slide-card shrink-0 snap-start w-full sm:w-[350px] md:w-[380px] lg:w-[400px] flex flex-col"
                    >
                      {t.type === 'video' ? (
                        <div className="group h-full bg-white rounded-2xl overflow-hidden shadow-xs hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col justify-between hover:-translate-y-1">
                          <div>
                            {/* Video Thumbnail Header */}
                            <div
                              className="relative aspect-[16/10] w-full overflow-hidden bg-slate-900 cursor-pointer"
                              onClick={() => setActiveVideoModal({ name: t.name, youtubeId: t.youtube_id })}
                            >
                              <img
                                src={t.image ? resolveImg(t.image) : (t.youtube_id ? `https://img.youtube.com/vi/${t.youtube_id}/hqdefault.jpg` : '/images/highlights/highlights-1.jpg')}
                                alt={t.name}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                loading="lazy"
                              />
                              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                                <div className="w-14 h-14 rounded-full bg-red-600/90 text-white flex items-center justify-center pl-1 shadow-lg group-hover:scale-110 group-hover:bg-red-600 transition-transform">
                                  <i className="fas fa-play text-base" />
                                </div>
                              </div>
                              <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur-md text-white font-semibold text-[11px] inline-flex items-center gap-1.5 border border-white/15">
                                <i className="fab fa-youtube text-red-500" />
                                <span>Video Story</span>
                              </div>
                            </div>

                            {/* Card Content */}
                            <div className="p-5 md:p-6">
                              <div className="flex gap-1 text-amber-400 text-xs mb-3">
                                {[...Array(t.rating || 5)].map((_, i) => (
                                  <i key={i} className="fas fa-star" />
                                ))}
                              </div>
                              {t.quote && (
                                <p className="text-body text-[14px] md:text-[15px] leading-relaxed italic line-clamp-3 mb-4">
                                  “{t.quote}”
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Author Footer */}
                          <div className="px-5 md:px-6 pb-5 md:pb-6 pt-3 border-t border-gray-100 flex items-center justify-between">
                            <div>
                              <h3 className="font-serif font-bold text-primary text-base leading-tight">
                                {t.name}
                              </h3>
                              {t.designation && (
                                <p className="text-xs text-muted mt-0.5">{t.designation}</p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setActiveVideoModal({ name: t.name, youtubeId: t.youtube_id })}
                              className="w-9 h-9 rounded-full bg-primary/5 text-primary hover:bg-primary hover:text-white flex items-center justify-center transition-colors shrink-0"
                              title="Watch video"
                            >
                              <i className="fas fa-play text-xs" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="group h-full bg-white rounded-2xl p-6 md:p-7 shadow-xs hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col justify-between hover:-translate-y-1">
                          <div>
                            {/* Top quotation and stars */}
                            <div className="flex items-center justify-between mb-4">
                              <div className="w-10 h-10 rounded-xl bg-secondary/15 text-secondary flex items-center justify-center">
                                <i className="fas fa-quote-left text-sm" />
                              </div>
                              <div className="flex gap-1 text-amber-400 text-xs">
                                {[...Array(t.rating || 5)].map((_, i) => (
                                  <i key={i} className="fas fa-star" />
                                ))}
                              </div>
                            </div>

                            {/* Quote text */}
                            <p className="text-body text-[14px] md:text-[15px] leading-relaxed italic mb-6">
                              “{t.quote}”
                            </p>
                          </div>

                          {/* Author Footer */}
                          <div className="pt-4 border-t border-gray-100 flex items-center gap-3.5">
                            {t.image ? (
                              <img
                                src={resolveImg(t.image)}
                                alt={t.name}
                                className="w-12 h-12 rounded-full object-cover border border-gray-200 shadow-2xs shrink-0"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-primary to-primary-light text-white font-serif font-bold text-base flex items-center justify-center shadow-2xs shrink-0">
                                {t.name?.charAt(0) || 'P'}
                              </div>
                            )}
                            <div>
                              <h3 className="font-serif font-bold text-primary text-base leading-tight">
                                {t.name}
                              </h3>
                              {t.designation && (
                                <p className="text-xs text-muted mt-0.5">{t.designation}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Slider Navigation Arrows */}
                {filteredTestimonials.length > 2 && (
                  <>
                    <button
                      type="button"
                      onClick={() => scrollTestimonials(-1)}
                      aria-label="Previous Testimonial"
                      className="absolute -left-2 sm:-left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-lg border border-gray-200 text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-all z-20 group"
                    >
                      <i className="fas fa-chevron-left text-sm group-hover:-translate-x-0.5 transition-transform" />
                    </button>
                    <button
                      type="button"
                      onClick={() => scrollTestimonials(1)}
                      aria-label="Next Testimonial"
                      className="absolute -right-2 sm:-right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-lg border border-gray-200 text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-all z-20 group"
                    >
                      <i className="fas fa-chevron-right text-sm group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </section>
        </Reveal>
      )}

      {/* VIDEO LIGHTBOX / MODAL */}
      {activeVideoModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xs animate-fade-in"
          onClick={() => setActiveVideoModal(null)}
        >
          <div
            className="relative w-full max-w-3xl bg-black rounded-2xl overflow-hidden shadow-2xl border border-white/10"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3.5 bg-[#0c1a30] border-b border-white/10">
              <div className="text-white font-medium text-sm truncate flex items-center gap-2">
                <i className="fab fa-youtube text-red-500 text-base" />
                <span className="font-semibold">{activeVideoModal.name ? `${activeVideoModal.name} — Testimonial` : 'Video Testimonial'}</span>
              </div>
              <button
                type="button"
                onClick={() => setActiveVideoModal(null)}
                className="text-white/70 hover:text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
                aria-label="Close video"
              >
                <i className="fas fa-times text-base" />
              </button>
            </div>
            <div className="relative aspect-video w-full bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${activeVideoModal.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                title={activeVideoModal.name || "Video Testimonial"}
                className="w-full h-full absolute inset-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      <Lightbox
        open={lightboxOpen}
        items={lightboxItems}
        initialIndex={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
      />

      {isPopupEnabled && (
        <ImagePopupModal
          image={popupImage}
          delayMs={popupDelay}
          onImageClick={() => {
            if (popupAction === 'enquiry') {
              openEnquiryPopup();
            }
          }}
        />
      )}
    </>
  );
};

export default Home;
